import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiCircleCreditService } from '../api/api-circle-credit.service';
import { LeaseDetailService } from './lease-detail.service';
import { PFFormat, PMFormat, PMFormatCredit, PMFormatCreditDetail, EntityType } from '../../interfaces/circle-credit.interface';

@Injectable({
  providedIn: 'root'
})
export class CircleCreditService {
  // Estados de carga (solo para guardado)
  loading = false;
  
  // Estado de loading para guardado con BehaviorSubject
  public isSaving$ = new BehaviorSubject<boolean>(false);
  
  // Estado del modal unificado
  showEditModal = false;
  
  // Datos actuales
  currentCircleCredit: PFFormat | PMFormat | null = null;
  currentEntityType: EntityType = 'Física';
  
  // Formulario unificado
  editForm!: FormGroup;
  
  constructor(
    private fb: FormBuilder,
    private apiCircleCreditService: ApiCircleCreditService,
    private messageService: MessageService,
    private leaseDetailService: LeaseDetailService
  ) {
    // Inicializar con formulario PF por defecto para mantener compatibilidad
    this.initializeForm();
  }

  // Método público para reinicializar formulario según tipo de entidad
  reinitializeFormForEntity(entityType: EntityType) {
    this.currentEntityType = entityType;
    
    if (entityType === 'Física') {
      this.initializeForm();
    } else {
      this.initializePMForm();
    }
  }

  private initializeForm() {
    this.editForm = this.fb.group({
      // Campos base
      // PF Format Structure - Solo las 6 secciones del backend
      header: this.fb.group({
        grantorKey: ['', Validators.required],
        grantorName: [''],
        mediaIdentifier: [''],
        extractionDate: [''],
        grantorNote: [''],
        version: ['']
      }),
      
      personalData: this.fb.group({
        lastNameFather: [''],
        lastNameMother: [''],
        additionalLastName: [''],
        givenNames: [''],
        birthDate: [''],
        rfc: [''],
        curp: [''],
        socialSecurityNumber: [''],
        nationality: [''],
        residence: [''],
        driverLicenseNumber: [''],
        maritalStatus: [''],
        sex: [''],
        voterKeyIFE: [''],
        dependentsNumber: [0],
        deathDate: [''],
        deathIndicator: [false],
        personType: ['']
      }),
      
      address: this.fb.group({
        address: [''],
        neighborhood: [''],
        municipality: [''],
        city: [''],
        state: [''],
        postalCode: [''],
        residenceDate: [''],
        phoneNumber: [''],
        addressType: [''],
        settlementType: [''],
        addressOrigin: ['']
      }),
      
      employment: this.fb.group({
        companyName: [''],
        address: [''],
        neighborhood: [''],
        municipality: [''],
        city: [''],
        state: [''],
        postalCode: [''],
        phoneNumber: [''],
        extension: [''],
        fax: [''],
        position: [''],
        hiringDate: [''],
        paymentFrequency: [''],
        monthlyIncome: [0],
        currency: ['MXN']
      }),
      
      accountDetail: this.fb.group({
        grantorKey: [''],
        grantorName: [''],
        accountNumber: [''],
        previousAccountNumber: [''],
        contractType: [''],
        accountType: [''],
        accountStatus: [''],
        openingDate: [''],
        lastPaymentDate: [''],
        lastPurchaseDate: [''],
        closingDate: [''],
        statementDate: [''],
        guarantee: [''],
        maxCredit: [0],
        currentBalance: [0],
        creditLimit: [0],
        pastDueBalance: [0],
        pastDuePaymentsNumber: [0],
        currentPayment: [0],
        paymentsHistory: [''],
        observationKey: [''],
        reportedPaymentsTotal: [0],
        previousGrantorKey: [''],
        previousGrantorName: [''],
        firstDefaultDate: [''],
        outstandingBalance: [0],
        lastPaymentAmount: [0],
        pastDueEntryDate: [''],
        interestsAmount: [0],
        interestsPaymentForm: [''],
        daysPastDue: [0],
        termMonths: [1],
        originationAmount: [0],
        consumerEmail: [''],
        canStatus: [''],
        priorityOrderOrigin: [''],
        priorityOrderCurrent: [''],
        canOpeningDate: [''],
        canCancellationDate: ['']
      }),
      
      controlFigures: this.fb.group({
        totalCurrentBalances: [null],
        totalPastDueBalances: [null],
        totalNameElementsReported: [null],
        totalAddressElementsReported: [null],
        totalEmploymentElementsReported: [null],
        totalAccountElementsReported: [null],
        grantorName: [''],
        returnAddress: [null]
      }),
      
      
      // Solo campos PFFormat - estructura limpia para backend
    });
  }

  // Método separado para inicializar formulario PM
  private initializePMForm() {
    this.editForm = this.fb.group({
      // Header para PM (campos específicos según backend PMFormat)
      header: this.fb.group({
        grantor: ['', Validators.required],                    // [ENCABEZADO] "Otorgante"
        previousGrantor: [''],                                 // [ENCABEZADO] "Otorgante Anterior"
        grantorName: [''],                                     // [ENCABEZADO] "NombreOtorgante"
        institution: [''],                                     // [ENCABEZADO] "Institucion"
        format: [''],                                          // [ENCABEZADO] "Formato"
        generationDate: [''],                                  // [ENCABEZADO] "Fecha generacion"
        reportingPeriod: [''],                                 // [ENCABEZADO] "Periodo que reporta"
        version: ['']                                          // [ENCABEZADO] "Versión"
      }),
      
      // Company (empresa) - sección específica de PM
      company: this.fb.group({
        rfc: ['', [Validators.required, Validators.minLength(12), Validators.maxLength(13)]],
        curp: [''],
        companyName: ['', Validators.required],
        firstName: [''],
        middleName: [''],
        lastNameFather: [''],
        lastNameMother: [''],
        nationality: [''],
        portfolioRating: [''],
        banxicoKey1: [''],
        banxicoKey2: [''],
        banxicoKey3: [''],
        address1: [''],
        address2: [''],
        neighborhood: [''],
        municipality: [''],
        city: [''],
        state: [''],
        postalCode: [''],
        phone: [''],
        extension: [''],
        fax: [''],
        clientType: [''],
        foreignState: [''],
        country: [''],
        mobilePhone: [''],
        email: ['']
      }),
      
      // Shareholder (accionista) - sección específica de PM
      shareholder: this.fb.group({
        rfc: [''],
        curp: [''],
        companyName: [''],
        firstName: [''],
        middleName: [''],
        lastNameFatherShareholder: [''],
        lastNameMotherShareholder: [''],
        percentage: [null],
        address1: [''],
        address2: [''],
        neighborhood: [''],
        municipality: [''],
        city: [''],
        state: [''],
        postalCode: [''],
        phone: [''],
        extension: [''],
        fax: [''],
        clientType: [''],
        foreignState: [''],
        country: ['']
      }),
      
      // Credits (créditos) - FormArray para PM
      credits: this.fb.array([]),
      
      // Guarantor (aval) - sección específica de PM
      guarantor: this.fb.group({
        rfc: [''],
        curp: [''],
        companyName: [''],
        firstName: [''],
        middleName: [''],
        lastNameFatherGuarantor: [''],
        lastNameMotherGuarantor: [''],
        address1: [''],
        address2: [''],
        neighborhood: [''],
        municipality: [''],
        city: [''],
        state: [''],
        postalCode: [''],
        phone: [''],
        extension: [''],
        fax: [''],
        clientType: [''],
        foreignState: [''],
        country: ['']
      })
    });
  }

  // Método principal para abrir modal de edición
  openEditModal(creditCircleData: PFFormat | PMFormat, creditCircleType: 'PFFormat' | 'PMFormat') {
    this.setCircleCreditData(creditCircleData, creditCircleType);
    this.showEditModal = true;
  }

  // Mapear datos del círculo de crédito directamente al formulario
  private setCircleCreditData(data: PFFormat | PMFormat, type: 'PFFormat' | 'PMFormat') {
    this.currentCircleCredit = data;
    this.currentEntityType = type === 'PFFormat' ? 'Física' : 'Moral';
    
    // Reinicializar formulario según tipo de entidad
    this.reinitializeFormForEntity(this.currentEntityType);
    
    if (!data || !this.editForm) {
      return;
    }

    try {
      if (type === 'PFFormat') {
        // MAPEO PF - MANTENER SIN CAMBIOS
        const pfData = data as PFFormat;
        
        // Solo mapear datos de las 6 secciones PF
        
        // Header
        if (pfData.header) {
          this.editForm.get('header')?.patchValue(pfData.header);
        }
        
        // Personal Data
        if (pfData.personalData) {
          this.editForm.get('personalData')?.patchValue(pfData.personalData);
        }
        
        // Address
        if (pfData.address) {
          this.editForm.get('address')?.patchValue(pfData.address);
        }
        
        // Employment
        if (pfData.employment) {
          this.editForm.get('employment')?.patchValue(pfData.employment);
        }
        
        // Account Detail
        if (pfData.accountDetail) {
          this.editForm.get('accountDetail')?.patchValue(pfData.accountDetail);
        }
        
        // Control Figures
        if (pfData.controlFigures) {
          this.editForm.get('controlFigures')?.patchValue(pfData.controlFigures);
        }
        
      } else {
        // MAPEO PM - datos del backend (sin header al nivel raíz)
        const pmData = data as PMFormat;
        
        // Header PM - usar valores del backend si existen
        const headerData = {
          grantor: pmData.grantor || '',
          previousGrantor: pmData.previousGrantor || '',
          grantorName: pmData.grantorName || '',
          institution: pmData.institution || '',
          format: pmData.format || 'PM',
          generationDate: pmData.generationDate ? new Date(pmData.generationDate) : null,
          reportingPeriod: pmData.reportingPeriod || '',
          version: pmData.version || '1.0'
        };
        this.editForm.get('header')?.patchValue(headerData);
        
        // Company
        if (pmData.company) {
          this.editForm.get('company')?.patchValue(pmData.company);
        }
        
        // Shareholder
        if (pmData.shareholder) {
          this.editForm.get('shareholder')?.patchValue(pmData.shareholder);
        }
        
        // Guarantor
        if (pmData.guarantor) {
          this.editForm.get('guarantor')?.patchValue(pmData.guarantor);
        }
        
        // Credits array
        if (pmData.credits && Array.isArray(pmData.credits)) {
          this.setPMCreditsArray(pmData.credits);
        }
      }
      
    } catch (error) {
      console.error('Error setting circle credit data:', error);
    }
  }
  
  // Método específico para establecer array de créditos PM
  private setPMCreditsArray(credits: PMFormatCredit[]) {
    const creditsFormArray = this.editForm?.get('credits') as FormArray;
    if (!creditsFormArray) return;

    // Limpiar array existente
    while (creditsFormArray.length !== 0) {
      creditsFormArray.removeAt(0);
    }

    // Agregar cada crédito PM
    credits.forEach(credit => {
      const creditFormGroup = this.fb.group({
        // Campos de PMFormatCredit según backend
        rfc: [credit.rfc || ''],
        creditExperiences: [credit.creditExperiences || ''],
        contractNumber: [credit.contractNumber || ''],
        previousContractNumber: [credit.previousContractNumber || ''],
        openingDate: [credit.openingDate || null],  // Fechas como null
        termMonths: [credit.termMonths || 0],
        creditType: [credit.creditType || ''],
        initialBalance: [credit.initialBalance || 0],
        currency: [credit.currency || ''],
        paymentsNumber: [credit.paymentsNumber || 0],
        paymentFrequency: [credit.paymentFrequency || ''],
        paymentAmount: [credit.paymentAmount || 0],
        lastPaymentDate: [credit.lastPaymentDate || null],  // Fechas como null
        restructuringDate: [credit.restructuringDate || null],  // Fechas como null
        cashPayment: [credit.cashPayment || ''],
        settlementDate: [credit.settlementDate || null],  // Fechas como null
        writeOff: [credit.writeOff || ''],
        dation: [credit.dation || ''],
        lossOrCharge: [credit.lossOrCharge || ''],
        observationKey: [credit.observationKey || ''],
        special: [credit.special || ''],
        firstDefaultDate: [credit.firstDefaultDate || null],  // Fechas como null
        outstandingBalance: [credit.outstandingBalance || 0],
        maxCreditUsed: [credit.maxCreditUsed || 0],
        pastDuePortfolioEntryDate: [credit.pastDuePortfolioEntryDate || null],  // Fechas como null
        details: this.fb.array(credit.details?.map((detail: PMFormatCreditDetail) => this.fb.group({
          rfc: [detail.rfc || ''],
          contractNumber: [detail.contractNumber || ''],
          overdueDays: [detail.overdueDays || 0],
          amount: [detail.amount || 0],
          interests: [detail.interests || 0]
        })) || [])
      });
      
      creditsFormArray.push(creditFormGroup);
    });
  }

  // Helper methods para verificar tipos
  private isPFFormat(data: any): data is PFFormat {
    return data && data.header && data.personalData;
  }

  private isPMFormat(data: any): data is PMFormat {
    return data && data.company && !data.personalData;
  }

  // Configurar array de créditos
  private setCreditsArray(credits: any[]) {
    const creditsFormArray = this.editForm.get('credits') as FormArray;
    creditsFormArray.clear();
    
    credits.forEach(credit => {
      const creditGroup = this.fb.group({
        grantorKey: [credit.grantorKey || ''],
        grantorName: [credit.grantorName || ''],
        accountNumber: [credit.accountNumber || ''],
        previousAccountNumber: [credit.previousAccountNumber || ''],
        contractType: [credit.contractType || ''],
        accountType: [credit.accountType || ''],
        accountStatus: [credit.accountStatus || ''],
        openingDate: [credit.openingDate || ''],
        lastPaymentDate: [credit.lastPaymentDate || ''],
        lastPurchaseDate: [credit.lastPurchaseDate || ''],
        closingDate: [credit.closingDate || ''],
        statementDate: [credit.statementDate || ''],
        guarantee: [credit.guarantee || ''],
        maxCredit: [credit.maxCredit || 0],
        currentBalance: [credit.currentBalance || 0],
        creditLimit: [credit.creditLimit || 0],
        pastDueBalance: [credit.pastDueBalance || 0],
        pastDuePaymentsNumber: [credit.pastDuePaymentsNumber || 0],
        currentPayment: [credit.currentPayment || 0],
        paymentsHistory: [credit.paymentsHistory || ''],
        observationKey: [credit.observationKey || ''],
        reportedPaymentsTotal: [credit.reportedPaymentsTotal || 0],
        previousGrantorKey: [credit.previousGrantorKey || ''],
        previousGrantorName: [credit.previousGrantorName || ''],
        firstDefaultDate: [credit.firstDefaultDate || ''],
        outstandingBalance: [credit.outstandingBalance || 0],
        lastPaymentAmount: [credit.lastPaymentAmount || 0],
        pastDueEntryDate: [credit.pastDueEntryDate || ''],
        interestsAmount: [credit.interestsAmount || 0],
        interestsPaymentForm: [credit.interestsPaymentForm || ''],
        daysPastDue: [credit.daysPastDue || 0],
        termMonths: [credit.termMonths || 1],
        originationAmount: [credit.originationAmount || 0],
        consumerEmail: [credit.consumerEmail || ''],
        canStatus: [credit.canStatus || ''],
        priorityOrderOrigin: [credit.priorityOrderOrigin || ''],
        priorityOrderCurrent: [credit.priorityOrderCurrent || ''],
        canOpeningDate: [credit.canOpeningDate || ''],
        canCancellationDate: [credit.canCancellationDate || '']
      });
      
      creditsFormArray.push(creditGroup);
    });
  }

  // Guardar cambios - Recibe MessageService del componente padre
  saveCircleCreditData(messageService?: MessageService) {
    // Validar según tipo de entidad
    if (!this.validateFormData()) {
      const msgService = messageService || this.messageService;
      msgService.add({
        severity: 'warn',
        summary: 'Formulario Incompleto',
        detail: this.getValidationMessage(),
        life: 4000
      });
      return;
    }
    
    if (this.editForm.valid && this.currentCircleCredit) {
      // Activar loading state
      this.isSaving$.next(true);
      
      const formData = this.buildSavePayload();
      
      const apiCall = this.currentEntityType === 'Física'
        ? this.apiCircleCreditService.updatePFFormat(this.currentCircleCredit._id!, formData)
        : this.apiCircleCreditService.updatePMFormat(this.currentCircleCredit._id!, formData);

      apiCall.subscribe({
        next: (response) => {
          // Desactivar loading state
          this.isSaving$.next(false);
          
          // Actualizar datos locales con la data del response (response = { error, data, msg })
          this.updateLocalData(response.data);
          
          // Mensaje de éxito específico según tipo de entidad
          const successMessage = this.currentEntityType === 'Física' 
            ? 'Información de Persona Física actualizada correctamente'
            : 'Información de Persona Moral actualizada correctamente';
          
          // Usar MessageService del componente padre si está disponible, sino el propio
          const msgService = messageService || this.messageService;
          msgService.add({
            severity: 'success',
            summary: 'Actualización Exitosa',
            detail: successMessage,
            life: 4000
          });
          
          this.closeEditModal();
        },
        error: (error) => {
          // Desactivar loading state en caso de error
          this.isSaving$.next(false);
          
          console.error('Error guardando círculo de crédito:', error);
          
          // Usar MessageService del componente padre si está disponible, sino el propio
          const msgService = messageService || this.messageService;
          msgService.add({
            severity: 'error',
            summary: 'Error al Guardar',
            detail: 'Error al guardar la información del círculo de crédito',
            life: 5000
          });
        }
      });
    }
  }

  // Validación específica según tipo de entidad
  private validateFormData(): boolean {
    if (this.currentEntityType === 'Física') {
      return this.validatePFForm();
    } else {
      return this.validatePMForm();
    }
  }

  private validatePFForm(): boolean {
    // Validaciones PF existentes
    return this.editForm.valid;
  }

  private validatePMForm(): boolean {
    if (!this.editForm) return false;
    
    // Validaciones específicas para PM
    const company = this.editForm.get('company');
    const header = this.editForm.get('header');
    
    // Validar campos críticos de PM
    if (!company?.get('companyName')?.value) return false;
    if (!company?.get('rfc')?.value) return false;
    if (!header?.get('grantor')?.value) return false;
    
    return this.editForm.valid;
  }

  private getValidationMessage(): string {
    if (this.currentEntityType === 'Física') {
      return 'Por favor complete todos los campos requeridos para Persona Física';
    } else {
      return 'Por favor complete: Razón Social, RFC de la empresa y Otorgante';
    }
  }

  // Construir payload específico según tipo de entidad
  private buildSavePayload(): PFFormat | PMFormat {
    const formValue = this.editForm.value;
    
    if (this.currentEntityType === 'Física') {
      // Payload PF - mantener estructura existente
      return {
        ...this.currentCircleCredit,
        header: formValue.header,
        personalData: formValue.personalData,
        address: formValue.address,
        employment: formValue.employment,
        accountDetail: formValue.accountDetail,
        controlFigures: formValue.controlFigures
      } as PFFormat;
    } else {
      // Payload PM - mapear campos del header al nivel raíz según backend schema
      return {
        // Mapear campos del header al nivel raíz según backend schema
        grantor: formValue.header?.grantor || '',
        previousGrantor: formValue.header?.previousGrantor || null,
        grantorName: formValue.header?.grantorName || null,
        institution: formValue.header?.institution || null,
        format: formValue.header?.format || null,
        generationDate: formValue.header?.generationDate instanceof Date
          ? formValue.header.generationDate.toISOString().split('T')[0]
          : formValue.header?.generationDate || null,
        reportingPeriod: formValue.header?.reportingPeriod || null,
        version: formValue.header?.version || null,
        
        // Mantener estructura existente
        company: formValue.company,
        shareholder: formValue.shareholder,
        credits: formValue.credits?.map((credit: any) => ({
          ...credit,
          // Asegurar que las fechas se envíen como null si están vacías
          openingDate: credit.openingDate || null,
          lastPaymentDate: credit.lastPaymentDate || null,
          restructuringDate: credit.restructuringDate || null,
          settlementDate: credit.settlementDate || null,
          firstDefaultDate: credit.firstDefaultDate || null,
          pastDuePortfolioEntryDate: credit.pastDuePortfolioEntryDate || null,
          details: credit.details || []
        })),
        guarantor: formValue.guarantor
      } as PMFormat;
    }
  }

  // Cerrar modal
  closeEditModal() {
    this.showEditModal = false;
    this.currentCircleCredit = null;
    this.editForm.reset();
  }

  // Getters para facilitar acceso a FormArrays
  get addressesArray(): FormArray {
    return this.editForm.get('addresses') as FormArray;
  }

  get employmentsArray(): FormArray {
    return this.editForm.get('employments') as FormArray;
  }

  get accountsArray(): FormArray {
    return this.editForm.get('accounts') as FormArray;
  }

  get creditsArray(): FormArray {
    return this.editForm.get('credits') as FormArray;
  }

  // ==================== MÉTODOS DE GESTIÓN DE CRÉDITOS PM ====================

  /**
   * Crea un nuevo FormGroup para un crédito con todos los campos del backend
   */
  private createCreditFormGroup(creditData?: any): FormGroup {
    return this.fb.group({
      rfc: [creditData?.rfc || ''],
      creditExperiences: [creditData?.creditExperiences || ''],
      contractNumber: [creditData?.contractNumber || ''],
      previousContractNumber: [creditData?.previousContractNumber || ''],
      openingDate: [creditData?.openingDate || null],
      termMonths: [creditData?.termMonths || 0],
      creditType: [creditData?.creditType || ''],
      initialBalance: [creditData?.initialBalance || 0],
      currency: [creditData?.currency || ''],
      paymentsNumber: [creditData?.paymentsNumber || 0],
      paymentFrequency: [creditData?.paymentFrequency || ''],
      paymentAmount: [creditData?.paymentAmount || 0],
      lastPaymentDate: [creditData?.lastPaymentDate || null],
      restructuringDate: [creditData?.restructuringDate || null],
      cashPayment: [creditData?.cashPayment || ''],
      settlementDate: [creditData?.settlementDate || null],
      writeOff: [creditData?.writeOff || ''],
      dation: [creditData?.dation || ''],
      lossOrCharge: [creditData?.lossOrCharge || ''],
      observationKey: [creditData?.observationKey || ''],
      special: [creditData?.special || ''],
      firstDefaultDate: [creditData?.firstDefaultDate || null],
      outstandingBalance: [creditData?.outstandingBalance || 0],
      maxCreditUsed: [creditData?.maxCreditUsed || 0],
      pastDuePortfolioEntryDate: [creditData?.pastDuePortfolioEntryDate || null],
      details: this.fb.array(creditData?.details?.map((detail: any) => this.createCreditDetailFormGroup(detail)) || [])
    });
  }

  /**
   * Crea un nuevo FormGroup para un detalle de crédito
   */
  private createCreditDetailFormGroup(detailData?: any): FormGroup {
    return this.fb.group({
      rfc: [detailData?.rfc || ''],
      contractNumber: [detailData?.contractNumber || ''],
      overdueDays: [detailData?.overdueDays || 0],
      amount: [detailData?.amount || 0],
      interests: [detailData?.interests || 0]
    });
  }

  /**
   * Agrega un nuevo crédito vacío al FormArray
   */
  addNewCredit(): void {
    const creditsArray = this.creditsArray;
    if (creditsArray) {
      const newCredit = this.createCreditFormGroup();
      creditsArray.push(newCredit);
    }
  }

  /**
   * Edita un crédito específico (abre modal de edición)
   */
  editCredit(index: number): void {
    // TODO: Implementar modal de edición específico
    // Por ahora, solo registra el evento para debugging
    console.log('Editando crédito en índice:', index);
  }

  /**
   * Elimina un crédito del FormArray
   */
  removeCredit(index: number): void {
    const creditsArray = this.creditsArray;
    if (creditsArray && index >= 0 && index < creditsArray.length) {
      creditsArray.removeAt(index);
    }
  }

  /**
   * Agrega un nuevo detalle a un crédito específico
   */
  addCreditDetail(creditIndex: number): void {
    const creditsArray = this.creditsArray;
    if (creditsArray && creditIndex >= 0 && creditIndex < creditsArray.length) {
      const creditFormGroup = creditsArray.at(creditIndex) as FormGroup;
      const detailsArray = creditFormGroup.get('details') as FormArray;
      if (detailsArray) {
        const newDetail = this.createCreditDetailFormGroup();
        detailsArray.push(newDetail);
      }
    }
  }

  /**
   * Elimina un detalle específico de un crédito
   */
  removeCreditDetail(creditIndex: number, detailIndex: number): void {
    const creditsArray = this.creditsArray;
    if (creditsArray && creditIndex >= 0 && creditIndex < creditsArray.length) {
      const creditFormGroup = creditsArray.at(creditIndex) as FormGroup;
      const detailsArray = creditFormGroup.get('details') as FormArray;
      if (detailsArray && detailIndex >= 0 && detailIndex < detailsArray.length) {
        detailsArray.removeAt(detailIndex);
      }
    }
  }

  // Método para actualizar datos locales después del guardado exitoso
  private updateLocalData(response: any): void {
    try {
      // Actualizar datos en LeaseDetailService para que las cards se refresquen
      if (this.leaseDetailService.data?.creditCircle) {
        // Crear nueva referencia del objeto completo para forzar change detection
        this.leaseDetailService.data = {
          ...this.leaseDetailService.data,
          creditCircle: {
            ...this.leaseDetailService.data.creditCircle,
            ...response
          }
        };
        
        // Actualizar también el registro actual
        this.currentCircleCredit = {
          ...this.currentCircleCredit,
          ...response
        } as PFFormat | PMFormat;
      }
    } catch (error) {
      console.warn('Error actualizando datos locales:', error);
    }
  }

  /**
   * Descarga el reporte de círculo de crédito en formato Excel
   */
  downloadReport(id: string): Observable<Blob> {
    return this.apiCircleCreditService.downloadReport(id);
  }
}
